/* eslint-disable import/order */
import React, { useRef, useEffect, useState } from 'react';
import './_TransactionTabsHeader.sass';
import IconWallet from '../../assets/icons/IconWallet';
import HeaderIconButton from '@pano-project/components/HeaderButtons/HeaderIconButton';
import { useAppSelector } from 'state/store-hooks';
import { shallowEqual } from 'react-redux';
import { useCashierHeaderEvents } from '@pano-project/hooks/useCashierHeaderEvents';
import { useBootHeaderSettings } from '@pano-project/components/CashierHeader/useBootHeaderSettings';
import { embeddedBaseCurrency } from '@pano-project/variables/boot-header-settings';
import { debounce } from 'utils/helper-functions/debounce';
import {
  TAB,
  TAB_LABEL_KEY,
  TRANSACTION_TYPE,
  getActiveIntentTab,
  getPrimaryIntent,
  getSecondaryIntents,
  mapIntentToTab,
} from '@pano-project/utils/intentMapping';
import { hasTriedToSubmit } from '@pano-project/utils/submissionAttempt';

interface Props {
  transactionType: string;
  errorInfo?: React.ReactNode;
  lang: (key: string) => string;
  className?: string;
  activeTab?: string;
  onTabClick?: (tab: string) => void;
  withPending?: boolean;
  showPaymentType?: boolean;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  backDisabled?: boolean;
  balance?: string;
  balancePrefix?: React.ReactNode;
  balanceSuffix?: React.ReactNode;
  balanceTabClassName?: string;
  hasBalanceError?: boolean;
  onBack?: () => void;
  // True while the page knows a submission is under way (see hasTriedToSubmit).
  submitLocked?: boolean;
  /**
   * The tabs ignore the player and are drawn locked, whether or not the bar has
   * a click handler, from a tab click until the gateways response arrives.
   */
  tabsDisabled?: boolean;
  /**
   * The ordered session intents known before the session reply (from the host
   * page boot configuration), passed by the start-up header only. They draw the
   * tabs in place of the stored session's intents and are never stored.
   */
  startupIntents?: string[];
}

const TransactionTabsHeader: React.FC<Props> = ({
  transactionType,
  errorInfo,
  lang,
  className = '',
  activeTab,
  onTabClick: onTabClickProp,
  withPending = false,
  showPaymentType = true,
  showBackButton: showBackButtonProp,
  showCloseButton: showCloseButtonProp,
  backDisabled,
  balance,
  balancePrefix,
  balanceSuffix,
  balanceTabClassName = '',
  hasBalanceError = false,
  onBack,
  submitLocked,
  tabsDisabled = false,
  startupIntents,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const depositTabRef = useRef<HTMLDivElement | null>(null);
  const withdrawTabRef = useRef<HTMLDivElement | null>(null);
  const balanceRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isTwoRowsHeader, setIsTwoRowsHeader] = useState(false);

  const headerButtons = useBootHeaderSettings();
  const showBackButton = showBackButtonProp ?? headerButtons.showBackButton;
  const showCloseButton = showCloseButtonProp ?? headerButtons.showCloseButton;
  const showAnyHeaderButton = showBackButton || showCloseButton;
  const hasHeaderElements = showPaymentType || !!balance;
  // After a submission attempt the bar offers no switch at all.
  const attempted = useAppSelector((state) =>
    hasTriedToSubmit(state, { submitting: submitLocked }),
  );
  const onTabClick = attempted ? undefined : onTabClickProp;
  const { baseCurrency } = useAppSelector(
    (state) => ({
      baseCurrency: state.profileInfo?.base_currency,
    }),
    shallowEqual,
  );
  const resolvedBaseCurrency = baseCurrency ?? embeddedBaseCurrency;

  const primaryIntent = useAppSelector(getPrimaryIntent);
  const secondaryIntents = useAppSelector(getSecondaryIntents, shallowEqual);
  // The tab the player is on in a multi-intent session; undefined otherwise.
  const activeIntentTab = useAppSelector(getActiveIntentTab);

  const { notify, notifyClose } = useCashierHeaderEvents();

  // The single tab a single-intent session shows, chosen from the session-wide
  // transaction type exactly as before. An empty transaction type shows no tab.
  const transactionTypeTab =
    transactionType === TRANSACTION_TYPE.PAY_IN
      ? TAB.DEPOSIT
      : transactionType === TRANSACTION_TYPE.PAY_OUT
        ? TAB.WITHDRAWAL
        : null;

  // A multi-intent session drives the tabs from the session intents instead:
  // the primary intent's tab comes first, then the extra intents' tabs. Two
  // intents that map to the same tab (payment and authorization) collapse into
  // one tab, which then falls back to the single-tab behaviour below.
  // Before the session reply the start-up header draws them from the boot list.
  const tabIntents =
    startupIntents ?? (secondaryIntents.length ? [primaryIntent, ...secondaryIntents] : []);
  const intentTabs = tabIntents.length
    ? tabIntents.map(mapIntentToTab).filter((tab, index, tabs) => tabs.indexOf(tab) === index)
    : [];

  const allTabs =
    intentTabs.length > 1 ? intentTabs : transactionTypeTab ? [transactionTypeTab] : [];

  const tabRefs: Record<string, React.MutableRefObject<HTMLDivElement | null>> = {
    [TAB.DEPOSIT]: depositTabRef,
    [TAB.WITHDRAWAL]: withdrawTabRef,
  };

  // A screen that names no tab gets the tab the player is on in a multi-intent
  // session, so every screen agrees with the tab switch. Otherwise, and on the
  // start-up header before the session intents are known, the first tab stays
  // the default. A tab the screen names always wins.
  const defaultTab =
    activeIntentTab !== undefined && allTabs.includes(activeIntentTab)
      ? activeIntentTab
      : allTabs[0];

  // After a submission attempt only the tab the player is on stays; a named tab
  // that is not in the list falls back to the default one.
  const ownTab = activeTab !== undefined && allTabs.includes(activeTab) ? activeTab : defaultTab;
  const tabs = attempted ? allTabs.filter((tab) => tab === ownTab) : allTabs;

  // The payment type setting decides whether the transaction tabs are drawn at
  // all; the balance tab and the header buttons stay either way.
  const visibleTabs = showPaymentType ? tabs : [];

  const isActive = (tab: string): boolean =>
    attempted ? tab === ownTab : activeTab === undefined ? tab === defaultTab : activeTab === tab;

  const handleKeyDown = (tab: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTabClick?.(tab);
    }
  };

  const headerClass = [
    'trx-tabs',
    withPending ? 'trx-tabs--with-pending' : '',
    hasHeaderElements ? '' : 'trx-tabs--no-divider',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const balanceTabClass = [
    'trx-tabs__tab',
    'trx-tabs__tab--balance',
    hasBalanceError ? '_balance-error' : '',
    balanceTabClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const handleCloseButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    notifyClose();
  };

  useEffect(() => {
    if (!showAnyHeaderButton) return;

    const containerElement = containerRef?.current;
    const backButtonElement = backButtonRef?.current;
    const depositTabElement = depositTabRef?.current;
    const withdrawTabElement = withdrawTabRef?.current;
    const balanceElement = balanceRef?.current;
    const closeButtonElement = closeButtonRef?.current;

    let cancelled = false;

    const checkElementsWidth = debounce(() => {
      if (cancelled) return;
      const containerWidth = containerElement?.offsetWidth || 0;
      const backButtonWidth = backButtonElement?.offsetWidth || 0;
      const depositTabWidth = depositTabElement?.offsetWidth || 0;
      const withdrawTabWidth = withdrawTabElement?.offsetWidth || 0;
      const balanceWidth = balanceElement?.offsetWidth || 0;
      const closeButtonWidth = closeButtonElement?.offsetWidth || 0;

      const contentWidth =
        backButtonWidth +
        (depositTabWidth ? depositTabWidth + 12 : depositTabWidth) +
        (withdrawTabWidth ? withdrawTabWidth + 12 : withdrawTabWidth) +
        balanceWidth +
        closeButtonWidth;

      setIsTwoRowsHeader(!!containerWidth && containerWidth < contentWidth + 32);
    }, 50);

    const resizeObserver = new ResizeObserver(() => {
      checkElementsWidth();
    });

    containerElement && resizeObserver.observe(containerElement);
    backButtonElement && resizeObserver.observe(backButtonElement);
    depositTabElement && resizeObserver.observe(depositTabElement);
    withdrawTabElement && resizeObserver.observe(withdrawTabElement);
    balanceElement && resizeObserver.observe(balanceElement);
    closeButtonElement && resizeObserver.observe(closeButtonElement);

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
    };
  }, [isTwoRowsHeader]);

  const handleBackButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onBack) {
      onBack();
      return;
    }
    notify('cashier_back_button');
  };

  const renderBackButton = () => (
    <HeaderIconButton
      variant="back"
      buttonRef={backButtonRef}
      onClick={handleBackButtonClick}
      disabled={backDisabled}
      className="trx-tabs__button"
    />
  );

  const renderCloseButton = () => (
    <HeaderIconButton
      variant="close"
      buttonRef={closeButtonRef}
      onClick={handleCloseButtonClick}
      className="trx-tabs__button"
    />
  );

  return (
    <div className={headerClass}>
      {isTwoRowsHeader && (
        <div className="trx-tabs__buttons-wrapper">
          {showBackButton && renderBackButton()}
          {showCloseButton && renderCloseButton()}
        </div>
      )}
      <div className="trx-tabs__list" ref={containerRef}>
        {showBackButton && !isTwoRowsHeader && renderBackButton()}
        {errorInfo}
        {visibleTabs.map((tab) => (
          <div
            key={tab}
            ref={tabRefs[tab]}
            className={`trx-tabs__tab${visibleTabs.length === 1 ? ' _single' : ''}${isActive(tab) ? ' _active' : ' _inactive'}${
              tabsDisabled ? ' _disabled' : ''
            }`}
            {...(onTabClick &&
              !tabsDisabled && {
                onClick: () => onTabClick(tab),
                role: 'button',
                tabIndex: 0,
                onKeyDown: handleKeyDown(tab),
              })}
            {...(tabsDisabled && { 'aria-disabled': true })}
            {...(onTabClick &&
              tabsDisabled && {
                role: 'button',
                tabIndex: -1,
              })}
          >
            {lang(TAB_LABEL_KEY[tab])}
          </div>
        ))}
        <div className={balanceTabClass} ref={balanceRef}>
          <span className="trx-tabs__base-currency">{resolvedBaseCurrency}</span>
          {balancePrefix}

          {balance && (
            <div
              className={'balance-block'}
              {...(onTabClick &&
                balance && {
                  onClick: () => onTabClick('balance'),
                  role: 'button',
                  tabIndex: 0,
                  onKeyDown: handleKeyDown('balance'),
                })}
            >
              <IconWallet color="var(--neutral_7)" />

              <span>{balance}</span>
            </div>
          )}

          {balanceSuffix}
        </div>
        {showCloseButton && !isTwoRowsHeader && renderCloseButton()}
      </div>
    </div>
  );
};

export default TransactionTabsHeader;
